<script lang="ts">
  import { navigate } from 'svelte-routing';
  import { api } from '../api/client';
  import { groups } from '../stores/groups';
  import { isAuthenticated } from '../stores/auth';
  import PageHeader from './PageHeader.svelte';
  import { t } from '../i18n/index.ts';

  // FirstGroup stands in for the page while a signed-in member belongs to no
  // group (spec 001 FR-041, 001 FR-042): they create their first group, or
  // join one with an invite. It cannot be skipped; Settings stays reachable.
  // It waits for the groups to load, so a member whose groups could not be
  // read is not told they have none.
  let { children } = $props();

  const loaded = groups.loaded;
  let name = $state('');
  let error = $state('');
  let busy = $state(false);

  $effect(() => {
    if ($isAuthenticated && !$loaded) groups.load().catch(() => {});
  });

  // A signed-out visitor goes to sign-in before the page mounts, so it asks
  // the server for nothing it would be refused, and comes back afterwards.
  $effect(() => {
    if ($isAuthenticated) return;
    const here = window.location.pathname;
    navigate(here === '/' ? '/login' : `/login?next=${encodeURIComponent(here)}`, { replace: true });
  });

  const gated = $derived($isAuthenticated && $loaded && $groups.length === 0);
  // Whether this member may create groups comes from the server's setting,
  // read afresh when the prompt shows (001 FR-044, 001 FR-045); nothing is offered
  // until the server says, so no one sees a form the server would refuse.
  let canCreate = $state<boolean | null>(null);
  let meError = $state('');
  function readSetting() {
    meError = '';
    api.get<{ can_create_groups?: boolean }>('/auth/me').then((me) => (canCreate = me.can_create_groups !== false)).catch((e: any) => (meError = e.message));
  }
  $effect(() => {
    if (gated) readSetting();
  });

  async function create() {
    error = '';
    busy = true;
    try {
      await groups.create(name.trim());
    } catch (e: any) {
      error = e.message;
    } finally {
      busy = false;
    }
  }
</script>

{#if gated && canCreate === null}
  <div class="flex flex-col items-center justify-center gap-3 flex-1 p-4">
    {#if meError}
      <p class="text-sm" style="color: var(--theme-error);">{meError}</p>
      <button class="btn btn-secondary" onclick={readSetting}>{t('appRetry')}</button>
      <a class="btn btn-secondary" href="/settings">{t('appSettings')}</a>
    {:else}
      <p class="text-text-muted">{t('componentsFirstGroupLoading')}</p>
    {/if}
  </div>
{:else if gated}
  <div class="flex items-center justify-center flex-1 p-4">
    <div class="card p-8 w-full max-w-md">
      <PageHeader title={canCreate ? t('componentsFirstGroupCreateTitle') : t('componentsFirstGroupJoinTitle')} />
      {#if canCreate}
        <p class="text-sm text-text-muted mb-4">
          {t('componentsFirstGroupIntro')}
        </p>
        {#if error}<p class="text-sm mb-3" style="color: var(--theme-error);">{error}</p>{/if}
        <form class="flex flex-col gap-3" onsubmit={(e: SubmitEvent) => { e.preventDefault(); create(); }}>
          <label for="first-group" class="block text-sm font-medium">{t('componentsFirstGroupName')}</label>
          <input id="first-group" class="input w-full" bind:value={name} required maxlength="64" placeholder={t('componentsFirstGroupNamePlaceholder')} />
          <button type="submit" class="btn btn-primary w-full" disabled={busy || !name.trim()}>{t('componentsFirstGroupCreate')}</button>
        </form>
        <p class="text-sm text-text-muted mt-6">{t('componentsFirstGroupInvited')}</p>
      {:else}
        <p class="text-sm text-text-muted">
          {t('componentsFirstGroupAskInvite')}
        </p>
      {/if}
      <a class="btn btn-secondary w-full block text-center mt-4" href="/settings">{t('appSettings')}</a>
    </div>
  </div>
{:else if $isAuthenticated}
  {@render children?.()}
{/if}
